import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { toast } from 'sonner';
import { supabase } from '@/api/supabaseClient';

// Deep-link URL Google/Supabase redirects back to once sign-in completes
// on the native app -- Capacitor's custom URL scheme (android/app/src/
// main/res/values/strings.xml's custom_url_scheme, wired to an
// intent-filter on MainActivity in AndroidManifest.xml). Must also be
// added to the Supabase project's Authentication -> URL Configuration ->
// Redirect URLs allowlist, or Supabase will refuse to redirect to it.
const NATIVE_REDIRECT_URL = 'ai.prohaul.app://auth-callback';

// On the web this is a plain full-page redirect through Google and back
// -- supabase-js's default detectSessionInUrl picks up the resulting
// session automatically on page load, same as the existing password-
// reset/email-verification flows already relied on. No native-only
// plugin involved.
//
// Inside the Capacitor Android shell, a plain redirect can't land back
// in the app (there's no page load to detect it on) -- so instead this
// opens the OAuth URL in a system browser tab (@capacitor/browser, so
// the user sees Google's real address bar rather than an embedded
// WebView, which Google's own policy blocks for OAuth), and listens for
// the app to be re-opened via the custom-scheme redirect
// (@capacitor/app's appUrlOpen, wired once in App.jsx) to finish signing
// in by hand.
export async function signInWithGoogle() {
  if (!supabase) throw new Error('Auth is not configured (missing Supabase env vars).');

  if (!Capacitor.isNativePlatform()) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return;
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: NATIVE_REDIRECT_URL, skipBrowserRedirect: true },
  });
  if (error) throw error;
  if (!data?.url) throw new Error('Could not start Google sign-in.');
  await Browser.open({ url: data.url });
}

// Registered once at app startup (App.jsx). Ignores any deep link that
// isn't our own OAuth callback -- Android can hand this app other
// custom-scheme URLs too (e.g. a future share-target link), and this
// listener has to coexist with those, not assume every appUrlOpen event
// is a sign-in.
export function handleNativeAuthRedirect(url) {
  if (!url || !url.startsWith(NATIVE_REDIRECT_URL) || !supabase) return;
  // Browser.close() is fire-and-forget: on some Android versions the
  // Custom Tab has already auto-closed itself by the time this runs, and
  // calling it again is harmless either way.
  Browser.close().catch(() => {});
  // This client isn't configured with flowType: 'pkce' (see
  // supabaseClient.js), so signInWithOAuth's redirect carries the tokens
  // directly in the URL fragment (#access_token=...&refresh_token=...),
  // not a ?code= to exchange -- same implicit-flow shape the web path
  // already relies on supabase-js's detectSessionInUrl to parse
  // automatically from window.location.hash; there's no such automatic
  // parsing for a deep-link URL string, so this does it by hand.
  const hash = url.split('#')[1];
  if (!hash) return;
  const params = new URLSearchParams(hash);
  // Google/Supabase redirect back with an error in this same shape when
  // sign-in fails or the user cancels (e.g. closes the browser tab) --
  // previously silently ignored, which left the button stuck in its
  // loading state forever with no feedback (see CarrierAuth.jsx's own
  // resume-triggered reset for the case where the user backs out without
  // even a redirect firing at all).
  const errorDescription = params.get('error_description') || params.get('error');
  if (errorDescription) {
    toast.error(decodeURIComponent(errorDescription).replace(/\+/g, ' '));
    return;
  }
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return;
  // AuthContext's own onAuthStateChange listener picks up the resulting
  // session and drives routing from there, same as every other sign-in
  // path -- nothing else to do with the result here.
  supabase.auth.setSession({ access_token, refresh_token })
    .catch(() => toast.error('Could not complete Google sign-in.'));
}
